import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { db } from "../firebase";
import {
  collection, addDoc, onSnapshot, doc, deleteDoc,
  updateDoc, query, orderBy, writeBatch
} from "firebase/firestore";
import DocumentRequestModal from "./DocumentRequestModal";

const CLOUD_NAME = "dscxfsvrf";
const UPLOAD_PRESET = "ficsori-portal";
const ADMIN_EMAIL = "frank@ficsoriaccounting.com";

const DEFAULT_FOLDERS = [
  { name: "Bank Statements", icon: "🏦" },
  { name: "Checks & Receipt Images", icon: "🧾" },
];

const FOLDER_ICONS = {
  "Bank Statements":"🏦","Tax Returns":"📋","QBO / Bookkeeping":"📊",
  "Payroll":"💼","Loans":"🏛","CFO Reports & Scorecards":"📈",
  "Engagement Letters":"📄","Corporate Docs":"🏢","Checks & Receipt Images":"🧾",
  "1099 / Tax Forms":"📑","Contracts & Agreements":"📝","Other / Miscellaneous":"📎",
};

const S = {
  btn:{fontSize:11,padding:"4px 10px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif",whiteSpace:"nowrap"},
  btnPrimary:{fontSize:11,padding:"4px 10px",borderRadius:6,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif",whiteSpace:"nowrap"},
  input:{width:"100%",padding:"5px 8px",fontSize:12,border:"0.5px solid #ccc",borderRadius:6,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  actionBtn:{fontSize:11,padding:"5px 10px",borderRadius:6,border:"0.5px solid #ccc",background:"#f5f5f0",cursor:"pointer",fontFamily:"sans-serif",whiteSpace:"nowrap",display:"flex",flexDirection:"column",alignItems:"center",gap:2,minWidth:58},
};

function formatSize(bytes){
  if(!bytes) return "";
  if(bytes>1048576) return (bytes/1048576).toFixed(1)+" MB";
  return Math.round(bytes/1024)+" KB";
}
function fileIcon(name){
  const ext=(name||"").split(".").pop().toLowerCase();
  if(ext==="pdf") return "📄";
  if(["xlsx","xls","csv"].includes(ext)) return "📊";
  if(["doc","docx"].includes(ext)) return "📝";
  if(["jpg","jpeg","png","gif","webp"].includes(ext)) return "🖼";
  return "📎";
}
function detectYear(filename){
  const match = filename.match(/20\d{2}/);
  return match ? match[0] : new Date().getFullYear().toString();
}
function buildTree(folders, clientName){
  const roots = [];
  const map = {};
  folders.filter(f=>f.client===clientName).forEach(f=>{ map[f.firestoreId]=({...f, children:[]}); });
  Object.values(map).forEach(f=>{
    if(f.parentId && map[f.parentId]) map[f.parentId].children.push(f);
    else roots.push(f);
  });
  const sort = (arr) => arr.sort((a,b)=>a.name.localeCompare(b.name)).map(f=>({...f,children:sort(f.children)}));
  return sort(roots);
}

// ── Folder Tree Item ─────────────────────────────────────────────────────────
function FolderTreeItem({ folder, depth=0, selectedId, onSelect, onCreateSub, onRename, onDelete, isAdmin, onDrop, dropHighlight }){
  const [expanded, setExpanded] = useState(depth===0);
  const hasChildren = folder.children && folder.children.length>0;
  const isSelected = selectedId===folder.firestoreId;
  const isDropTarget = dropHighlight===folder.firestoreId;

  return (
    <div>
      <div
        style={{
          display:"flex",alignItems:"center",gap:4,
          padding:"5px 8px",paddingLeft:(depth*14+8)+"px",
          borderRadius:6,cursor:"pointer",
          background:isDropTarget?"#E6F1FB":isSelected?"#fdf0f0":"transparent",
          color:isDropTarget?"#185FA5":isSelected?"#C0111F":"#333",
          fontWeight:isSelected?500:400,
          border:isDropTarget?"1px dashed #185FA5":"1px solid transparent",
          transition:"all 0.1s",
        }}
        onClick={()=>{onSelect(folder);if(hasChildren)setExpanded(x=>!x);}}
        onDragOver={e=>{ e.preventDefault(); e.dataTransfer.dropEffect="move"; onDrop("over", folder); }}
        onDragLeave={()=>onDrop("leave", null)}
        onDrop={e=>{ e.preventDefault(); onDrop("drop", folder); }}
        onMouseEnter={e=>{ if(!isSelected&&!isDropTarget) e.currentTarget.style.background="#f5f5f0"; }}
        onMouseLeave={e=>{ if(!isSelected&&!isDropTarget) e.currentTarget.style.background="transparent"; }}
      >
        <span style={{fontSize:10,color:"#aaa",width:12,textAlign:"center",flexShrink:0}}
          onClick={e=>{e.stopPropagation();setExpanded(x=>!x);}}>
          {hasChildren?(expanded?"▼":"▶"):""}
        </span>
        <span style={{fontSize:14,flexShrink:0}}>{FOLDER_ICONS[folder.name]||"📁"}</span>
        <span style={{fontSize:12,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          {folder.name}
        </span>
        {isAdmin&&(
          <div style={{display:"flex",gap:2,flexShrink:0,opacity:0,transition:"opacity 0.15s"}} className="folder-actions"
            onMouseEnter={e=>e.currentTarget.style.opacity=1}
            onMouseLeave={e=>e.currentTarget.style.opacity=0}
            onClick={e=>e.stopPropagation()}>
            <span title="Add subfolder" style={{cursor:"pointer",fontSize:10,padding:"1px 4px"}}
              onClick={e=>{e.stopPropagation();onCreateSub(folder);}}>+</span>
            <span title="Rename" style={{cursor:"pointer",fontSize:10,padding:"1px 4px"}}
              onClick={e=>{e.stopPropagation();onRename(folder);}}>✏</span>
            <span title="Delete" style={{cursor:"pointer",fontSize:10,padding:"1px 4px",color:"#C0111F"}}
              onClick={e=>{e.stopPropagation();onDelete(folder);}}>✕</span>
          </div>
        )}
      </div>
      {expanded&&hasChildren&&folder.children.map(child=>(
        <FolderTreeItem key={child.firestoreId} folder={child} depth={depth+1}
          selectedId={selectedId} onSelect={onSelect} onCreateSub={onCreateSub}
          onRename={onRename} onDelete={onDelete} isAdmin={isAdmin}
          onDrop={onDrop} dropHighlight={dropHighlight}/>
      ))}
    </div>
  );
}

// ── Upload Modal ─────────────────────────────────────────────────────────────
function UploadModal({ folder, clientName, allClients, allFolders, onClose, onUploaded, isAdmin }){
  const [files, setFiles] = useState([]);
  const [client, setClient] = useState(clientName||"");
  const [targetFolder, setTargetFolder] = useState(folder||null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [done, setDone] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const folderRef = useRef();
  const clientFolders = allFolders ? allFolders.filter(f=>f.client===client) : [];

  const addFilesFromFolder = async (fileList, overrideName) => {
    const arr = Array.from(fileList);
    if(!arr.length) return;
    const folderName = overrideName || (arr[0].webkitRelativePath ? arr[0].webkitRelativePath.split('/')[0] : null);
    if(folderName && client){
      try{
        const ref = await addDoc(collection(db,"folders"),{
          name:folderName, client,
          parentId:targetFolder?.firestoreId||null,
          parentName:targetFolder?.name||null,
          createdAt:new Date().toISOString(),
        });
        setTargetFolder({firestoreId:ref.id, name:folderName});
      }catch(e){}
    }
    setFiles(prev=>[...prev,...arr.map(f=>({file:f,year:detectYear(f.name)}))]);
  };

  const addFiles = (list) => {
    setFiles(prev=>[...prev,...Array.from(list).map(f=>({file:f,year:detectYear(f.name)}))]);
  };

  const upload = async () => {
    if(!client){alert("Select a client.");return;}
    if(!files.length){alert("Add files.");return;}
    setUploading(true);
    const sanitize = s=>s.replace(/[^a-zA-Z0-9_\-]/g,"_").replace(/__+/g,"_");
    for(const {file,year} of files){
      try{
        const fp = targetFolder
          ? `ficsori-portal/${sanitize(client)}/${sanitize(targetFolder.name)}/${year}`
          : `ficsori-portal/${sanitize(client)}/misc/${year}`;
        const fd = new FormData();
        fd.append("file",file); fd.append("upload_preset",UPLOAD_PRESET); fd.append("folder",fp);
        const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,{method:"POST",body:fd});
        if(!res.ok){const e=await res.json();throw new Error(e?.error?.message||res.status);}
        const data = await res.json();
        setProgress(p=>({...p,[file.name]:100}));
        await addDoc(collection(db,"files"),{
          name:file.name, client, folderId:targetFolder?.firestoreId||null,
          folderName:targetFolder?.name||"Uncategorized", year,
          size:formatSize(file.size), url:data.secure_url,
          cloudinaryId:data.public_id, uploadedAt:new Date().toISOString(),
          version:1, versions:[{version:1,url:data.secure_url,uploadedAt:new Date().toISOString()}],
        });
        setDone(d=>[...d,file.name]);
      }catch(e){alert("Failed: "+file.name+": "+e.message);}
    }
    setUploading(false);
    onUploaded(); onClose();
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#fff",borderRadius:16,width:520,maxHeight:"85vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{padding:"14px 18px",borderBottom:"0.5px solid #e0dfd8",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:13,fontWeight:500}}>Upload Files or Folders</div>
          <button style={S.btn} onClick={onClose}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"14px 18px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:"#888",marginBottom:3}}>Client *</div>
              <select style={S.input} value={client} onChange={e=>{setClient(e.target.value);setTargetFolder(null);}}>
                <option value="">Select...</option>
                {allClients.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:"#888",marginBottom:3}}>Folder</div>
              <select style={S.input} value={targetFolder?.firestoreId||""} onChange={e=>{
                setTargetFolder(clientFolders.find(f=>f.firestoreId===e.target.value)||null);
              }}>
                <option value="">Root</option>
                {clientFolders.map(f=><option key={f.firestoreId} value={f.firestoreId}>{FOLDER_ICONS[f.name]||"📁"} {f.name}</option>)}
              </select>
            </div>
          </div>
          <div
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={async e=>{
              e.preventDefault();setDragOver(false);
              const items=Array.from(e.dataTransfer.items);
              const allF=[];let fname=null;
              const readEntry=en=>new Promise(res=>{
                if(en.isFile){en.file(f=>{allF.push(f);res();},()=>res());}
                else if(en.isDirectory){
                  const r=en.createReader();
                  const ra=cb=>{r.readEntries(async es=>{if(!es.length){cb();return;}await Promise.all(es.map(readEntry));ra(cb);});};
                  ra(res);
                }else res();
              });
              const entries=items.map(i=>i.webkitGetAsEntry()).filter(Boolean);
              if(entries.length===1&&entries[0].isDirectory)fname=entries[0].name;
              await Promise.all(entries.map(readEntry));
              if(allF.length){if(fname)addFilesFromFolder(allF,fname);else addFiles(allF);}
            }}
            style={{border:`1.5px dashed ${dragOver?"#C0111F":"#ccc"}`,borderRadius:10,padding:"20px",textAlign:"center",background:dragOver?"#fdf0f0":"#fafaf8",marginBottom:10}}>
            <div style={{fontSize:22,marginBottom:6}}>⬆</div>
            <div style={{fontSize:12,fontWeight:500,marginBottom:8}}>Drop files or folders, or browse:</div>
            <div style={{display:"flex",gap:8,justifyContent:"center"}}>
              <button style={S.btn} onClick={e=>{e.stopPropagation();fileRef.current.click();}}>📄 Files</button>
              <button style={S.btn} onClick={e=>{e.stopPropagation();folderRef.current.click();}}>📁 Folder</button>
            </div>
          </div>
          <input ref={fileRef} type="file" multiple style={{display:"none"}} onChange={e=>addFiles(e.target.files)}/>
          <input ref={folderRef} type="file" webkitdirectory="" style={{display:"none"}} onChange={e=>addFilesFromFolder(e.target.files)}/>
          {files.map(({file,year},i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"0.5px solid #f0efe8",fontSize:11}}>
              <span>{fileIcon(file.name)}</span>
              <div style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{file.name}</div>
              <span style={{color:"#888"}}>{formatSize(file.size)}</span>
              {progress[file.name]&&<span style={{color:"#888"}}>{progress[file.name]}%</span>}
              {done.includes(file.name)&&<span style={{color:"#3B6D11"}}>✓</span>}
              {!uploading&&<span style={{cursor:"pointer",color:"#aaa"}} onClick={()=>setFiles(f=>f.filter((_,j)=>j!==i))}>✕</span>}
            </div>
          ))}
        </div>
        <div style={{padding:"10px 18px",borderTop:"0.5px solid #e0dfd8",display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} onClick={upload} disabled={uploading||!files.length||!client}>
            {uploading?"Uploading...":   `Upload ${files.length} File${files.length!==1?"s":""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Dual Panel File Row ───────────────────────────────────────────────────────
function FileRow({ f, selected, onClick, onDragStart, panelSide }){
  const isSelected = selected;
  return (
    <tr
      draggable
      onDragStart={e=>{
        e.dataTransfer.effectAllowed="move";
        e.dataTransfer.setData("text/plain", f.firestoreId);
        onDragStart(f, panelSide);
      }}
      onClick={e=>{
        if(e.shiftKey || e.ctrlKey || e.metaKey) onClick(f, "toggle");
        else onClick(f, "single");
      }}
      style={{
        borderBottom:"0.5px solid #f5f5f0",
        background:isSelected?"#E6F1FB":"transparent",
        cursor:"pointer",
        userSelect:"none",
      }}
      onMouseEnter={e=>{ if(!isSelected) e.currentTarget.style.background="#f5f5f0"; }}
      onMouseLeave={e=>{ if(!isSelected) e.currentTarget.style.background="transparent"; }}
    >
      <td style={{padding:"6px 8px",width:24}}>
        <span style={{fontSize:16}}>{fileIcon(f.name)}</span>
      </td>
      <td style={{padding:"6px 4px",maxWidth:180}}>
        <div style={{fontSize:12,fontWeight:isSelected?500:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
        {f.uploadedByClient&&<span style={{fontSize:9,padding:"1px 5px",background:"#EAF3DE",color:"#3B6D11",borderRadius:6}}>Client</span>}
      </td>
      <td style={{padding:"6px 4px",fontSize:11,color:"#888",whiteSpace:"nowrap"}}>{f.folderName||"—"}</td>
      <td style={{padding:"6px 4px",fontSize:11,color:"#aaa",whiteSpace:"nowrap"}}>{f.size}</td>
      <td style={{padding:"6px 4px",fontSize:11,color:"#aaa",whiteSpace:"nowrap"}}>
        {f.uploadedAt?new Date(f.uploadedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"2-digit"}):"—"}
      </td>
    </tr>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────
function Panel({ side, label, clients, folders, files, selected, setSelected, activeFolder, setActiveFolder,
  onDragStart, onDropFolder, dropHighlight, isAdmin, onRenameFolder, onDeleteFolder, onCreateSub,
  search, setSearch }){

  const clientFolders = folders.filter(f=>f.client===label);
  const folderTree = useMemo(()=>label?buildTree(folders, label):[], [folders, label]);

  const displayFiles = useMemo(()=>{
    let list = files.filter(f=>f.client===label);
    if(activeFolder) list = list.filter(f=>f.folderId===activeFolder.firestoreId);
    if(search) list = list.filter(f=>f.name.toLowerCase().includes(search.toLowerCase()));
    return list;
  },[files, label, activeFolder, search]);

  const handleClick = (f, mode) => {
    if(mode==="toggle"){
      setSelected(prev=>{
        const next=new Set(prev);
        next.has(f.firestoreId)?next.delete(f.firestoreId):next.add(f.firestoreId);
        return next;
      });
    } else {
      setSelected(prev=>{
        if(prev.size===1&&prev.has(f.firestoreId)) return new Set();
        return new Set([f.firestoreId]);
      });
    }
  };

  return (
    <div style={{display:"flex",flex:1,minWidth:0,flexDirection:"column",overflow:"hidden"}}>
      {/* Panel header */}
      <div style={{background:"#1a1a18",padding:"4px 8px",display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
        <span style={{fontSize:11,color:"#aaa",fontWeight:500}}>{label||"No client"}</span>
        {activeFolder&&<>
          <span style={{color:"#555",fontSize:10}}>›</span>
          <span style={{fontSize:11,color:"#888"}}>{FOLDER_ICONS[activeFolder.name]||"📁"} {activeFolder.name}</span>
          <span style={{fontSize:10,color:"#555",cursor:"pointer",marginLeft:"auto"}} onClick={()=>setActiveFolder(null)}>✕ All</span>
        </>}
      </div>

      {/* Folder tree */}
      <div style={{background:"#f9f9f7",borderBottom:"0.5px solid #e0dfd8",maxHeight:140,overflowY:"auto",flexShrink:0}}>
        {!label&&<div style={{fontSize:11,color:"#aaa",padding:"8px 10px"}}>Select a client</div>}
        {label&&folderTree.length===0&&<div style={{fontSize:11,color:"#aaa",padding:"8px 10px"}}>No folders</div>}
        {label&&(
          <>
            <div
              style={{display:"flex",alignItems:"center",gap:6,padding:"4px 8px",cursor:"pointer",fontSize:11,
                background:!activeFolder?"#fdf0f0":"transparent",color:!activeFolder?"#C0111F":"#555"}}
              onClick={()=>setActiveFolder(null)}
              onDragOver={e=>{e.preventDefault();onDropFolder("over",null);}}
              onDragLeave={()=>onDropFolder("leave",null)}
              onDrop={e=>{e.preventDefault();onDropFolder("drop",null);}}
            >
              <span>📂</span><span>All Files</span>
            </div>
            {folderTree.map(f=>(
              <FolderTreeItem key={f.firestoreId} folder={f}
                selectedId={activeFolder?.firestoreId}
                onSelect={setActiveFolder}
                onCreateSub={onCreateSub}
                onRename={onRenameFolder}
                onDelete={onDeleteFolder}
                isAdmin={isAdmin}
                onDrop={onDropFolder}
                dropHighlight={dropHighlight}
              />
            ))}
          </>
        )}
      </div>

      {/* Search */}
      <div style={{padding:"5px 8px",borderBottom:"0.5px solid #e0dfd8",flexShrink:0}}>
        <div style={{position:"relative"}}>
          <input style={{...S.input,paddingLeft:22,fontSize:11}} placeholder="Search files..." value={search} onChange={e=>setSearch(e.target.value)}/>
          <span style={{position:"absolute",left:6,top:"50%",transform:"translateY(-50%)",color:"#aaa",fontSize:11}}>🔍</span>
        </div>
      </div>

      {/* File list */}
      <div style={{flex:1,overflowY:"auto"}}>
        {displayFiles.length===0?(
          <div style={{textAlign:"center",padding:"30px 0",color:"#aaa",fontSize:12}}>
            {label?"No files here":"Select a client"}
          </div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"#f5f5f0",position:"sticky",top:0}}>
                <th style={{width:24,padding:"5px 8px"}}></th>
                <th style={{textAlign:"left",padding:"5px 4px",fontSize:10,color:"#888",fontWeight:500}}>Name</th>
                <th style={{textAlign:"left",padding:"5px 4px",fontSize:10,color:"#888",fontWeight:500}}>Folder</th>
                <th style={{textAlign:"left",padding:"5px 4px",fontSize:10,color:"#888",fontWeight:500}}>Size</th>
                <th style={{textAlign:"left",padding:"5px 4px",fontSize:10,color:"#888",fontWeight:500}}>Date</th>
              </tr>
            </thead>
            <tbody>
              {displayFiles.map(f=>(
                <FileRow key={f.firestoreId} f={f}
                  selected={selected.has(f.firestoreId)}
                  onClick={handleClick}
                  onDragStart={onDragStart}
                  panelSide={side}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Main Files Component ──────────────────────────────────────────────────────
export default function Files({ assignedClients, userEmail, isAdmin: isAdminProp }){
  const isAdmin = isAdminProp !== false;
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Panel state
  const [leftClient, setLeftClient] = useState(()=>localStorage.getItem("ficsori_left_client")||"");
  const [rightClient, setRightClient] = useState(()=>localStorage.getItem("ficsori_right_client")||"");
  const [leftFolder, setLeftFolder] = useState(null);
  const [rightFolder, setRightFolder] = useState(null);
  const [leftSelected, setLeftSelected] = useState(new Set());
  const [rightSelected, setRightSelected] = useState(new Set());
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [activePanel, setActivePanel] = useState("right"); // which panel is "active" for action bar

  // Drag state
  const [draggingFile, setDraggingFile] = useState(null);
  const [draggingFromPanel, setDraggingFromPanel] = useState(null);
  const [dropHighlightLeft, setDropHighlightLeft] = useState(null);
  const [dropHighlightRight, setDropHighlightRight] = useState(null);

  // Modals
  const [showUpload, setShowUpload] = useState(false);
  const [showDocRequest, setShowDocRequest] = useState(false);
  const [view, setView] = useState("files");
  const [renameFile, setRenameFile] = useState(null);
  const [renameFileName, setRenameFileName] = useState("");
  const [renameFolder, setRenameFolder] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [createFolderModal, setCreateFolderModal] = useState(null); // {parent, client}
  const [newFolderName, setNewFolderName] = useState("");
  const [moveConfirm, setMoveConfirm] = useState(null); // {files, targetFolder}

  useEffect(()=>{
    const u1 = onSnapshot(query(collection(db,"folders"),orderBy("name")),
      snap=>setFolders(snap.docs.map(d=>({...d.data(),firestoreId:d.id}))));
    const u2 = onSnapshot(query(collection(db,"files"),orderBy("uploadedAt","desc")),
      snap=>{setFiles(snap.docs.map(d=>({...d.data(),firestoreId:d.id})));setLoading(false);},
      ()=>setLoading(false));
    const u3 = onSnapshot(query(collection(db,"auditLog"),orderBy("timestamp","desc")),
      snap=>setAuditLog(snap.docs.map(d=>({...d.data(),firestoreId:d.id}))));
    const u4 = onSnapshot(query(collection(db,"clients"),orderBy("name")),
      snap=>setClients(snap.docs.map(d=>d.data().name).filter(Boolean)));
    return ()=>{u1();u2();u3();u4();};
  },[]);

  const visibleClients = assignedClients || clients;

  // Active panel selections
  const activeSelected = activePanel==="left" ? leftSelected : rightSelected;
  const activeClient = activePanel==="left" ? leftClient : rightClient;
  const activeFolder = activePanel==="left" ? leftFolder : rightFolder;
  const activeSetSelected = activePanel==="left" ? setLeftSelected : setRightSelected;

  const getSelectedFiles = (side) => {
    const sel = side==="left" ? leftSelected : rightSelected;
    return files.filter(f=>sel.has(f.firestoreId));
  };

  const allActiveSelected = [...leftSelected, ...rightSelected].map(id=>files.find(f=>f.firestoreId===id)).filter(Boolean);
  const totalSelected = leftSelected.size + rightSelected.size;

  // ── Actions ─────────────────────────────────────────────────────────────────
  const doView = () => {
    const f = allActiveSelected[0];
    if(f) window.open(f.url, "_blank");
  };

  const doDownload = async () => {
    for(const f of allActiveSelected){
      try{
        const res = await fetch(f.url,{mode:"cors"});
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href=url; a.download=f.name;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(()=>URL.revokeObjectURL(url),1000);
      }catch(e){
        const a=document.createElement("a");a.href=f.url;a.download=f.name;a.target="_blank";
        document.body.appendChild(a);a.click();document.body.removeChild(a);
      }
    }
  };

  const doRename = () => {
    const f = allActiveSelected[0];
    if(!f){alert("Select a file to rename.");return;}
    setRenameFile(f); setRenameFileName(f.name);
  };

  const doDelete = async () => {
    if(!totalSelected){alert("Select files to delete.");return;}
    if(!window.confirm(`Delete ${totalSelected} file${totalSelected!==1?"s":""}? This cannot be undone.`)) return;
    for(const f of allActiveSelected){
      try{
        await deleteDoc(doc(db,"files",f.firestoreId));
        await addDoc(collection(db,"auditLog"),{
          action:"deleted",fileName:f.name,client:f.client,
          folder:f.folderName,user:userEmail,timestamp:new Date().toISOString()
        });
      }catch(e){}
    }
    setLeftSelected(new Set()); setRightSelected(new Set());
  };

  const doMove = () => {
    if(!totalSelected){alert("Select files to move.");return;}
    // Show folder picker — open move modal
    setMoveConfirm({files: allActiveSelected, targetFolder: null});
  };

  const doNewFolder = () => {
    const client = activeClient;
    if(!client){alert("Select a client first.");return;}
    setCreateFolderModal({parent:activeFolder, client});
    setNewFolderName("");
  };

  const doNewSubfolder = () => {
    if(!activeFolder){alert("Select a parent folder first.");return;}
    setCreateFolderModal({parent:activeFolder, client:activeClient});
    setNewFolderName("");
  };

  const doDefaults = async () => {
    const client = activeClient;
    if(!client){alert("Select a client first.");return;}
    const existing = folders.filter(f=>f.client===client&&!f.parentId).map(f=>f.name);
    const toCreate = [
      {name:"Bank Statements",icon:"🏦"},
      {name:"Checks & Receipt Images",icon:"🧾"},
    ].filter(f=>!existing.includes(f.name));
    if(!toCreate.length){alert("Default folders already exist.");return;}
    for(const f of toCreate){
      await addDoc(collection(db,"folders"),{
        name:f.name,client,parentId:null,parentName:null,
        icon:f.icon,createdAt:new Date().toISOString(),createdBy:userEmail||"Frank",
      });
    }
  };

  const saveRenameFile = async () => {
    if(!renameFileName.trim()) return;
    try{ await updateDoc(doc(db,"files",renameFile.firestoreId),{name:renameFileName.trim()}); setRenameFile(null); }
    catch(e){alert("Failed: "+e.message);}
  };

  const saveRenameFolder = async () => {
    if(!renameFolderName.trim()) return;
    try{ await updateDoc(doc(db,"folders",renameFolder.firestoreId),{name:renameFolderName.trim()}); setRenameFolder(null); }
    catch(e){alert("Failed: "+e.message);}
  };

  const createFolder = async () => {
    if(!newFolderName.trim()){alert("Enter a folder name.");return;}
    try{
      await addDoc(collection(db,"folders"),{
        name:newFolderName.trim(), client:createFolderModal.client,
        parentId:createFolderModal.parent?.firestoreId||null,
        parentName:createFolderModal.parent?.name||null,
        icon:FOLDER_ICONS[newFolderName]||"📁",
        createdAt:new Date().toISOString(), createdBy:userEmail||"Frank",
      });
      setCreateFolderModal(null);
    }catch(e){alert("Failed: "+e.message);}
  };

  const deleteFolder = async (folder) => {
    const hasFiles = files.some(f=>f.folderId===folder.firestoreId);
    const hasChildren = folders.some(f=>f.parentId===folder.firestoreId);
    if(hasFiles||hasChildren){alert(`Cannot delete "${folder.name}" — it contains ${hasFiles?"files":""}${hasFiles&&hasChildren?" and ":""}${hasChildren?"subfolders":""}.`);return;}
    if(!window.confirm(`Delete folder "${folder.name}"?`)) return;
    try{ await deleteDoc(doc(db,"folders",folder.firestoreId)); }
    catch(e){alert("Failed: "+e.message);}
  };

  const executeMoveFiles = async (targetFolder) => {
    const filesToMove = moveConfirm.files;
    try{
      const batch = writeBatch(db);
      filesToMove.forEach(f=>{
        batch.update(doc(db,"files",f.firestoreId),{
          folderId:targetFolder?.firestoreId||null,
          folderName:targetFolder?.name||"Uncategorized",
        });
      });
      await batch.commit();
      setMoveConfirm(null);
      setLeftSelected(new Set()); setRightSelected(new Set());
    }catch(e){alert("Failed to move: "+e.message);}
  };

  // ── Drag & Drop between panels ────────────────────────────────────────────
  const handleDragStart = (file, fromPanel) => {
    setDraggingFile(file);
    setDraggingFromPanel(fromPanel);
  };

  const handleFolderDrop = (side) => (action, folder) => {
    if(action==="over"){
      if(side==="left") setDropHighlightLeft(folder?.firestoreId||"root");
      else setDropHighlightRight(folder?.firestoreId||"root");
    } else if(action==="leave"){
      if(side==="left") setDropHighlightLeft(null);
      else setDropHighlightRight(null);
    } else if(action==="drop"){
      if(side==="left") setDropHighlightLeft(null);
      else setDropHighlightRight(null);
      if(!draggingFile) return;
      const fromSel = draggingFromPanel==="left" ? leftSelected : rightSelected;
      let filesToMove = [];
      if(fromSel.has(draggingFile.firestoreId)){
        filesToMove = files.filter(f=>fromSel.has(f.firestoreId));
      } else {
        filesToMove = [draggingFile];
      }
      // Show simple confirmation with target pre-set
      const targetLabel = folder ? folder.name : "Root";
      if(window.confirm(`Move ${filesToMove.length} file${filesToMove.length!==1?"s":""} to "${targetLabel}"?`)){
        const batch = writeBatch(db);
        filesToMove.forEach(f=>{
          batch.update(doc(db,"files",f.firestoreId),{
            folderId:folder?.firestoreId||null,
            folderName:folder?.name||"Uncategorized",
          });
        });
        batch.commit().catch(e=>alert("Move failed: "+e.message));
        setLeftSelected(new Set()); setRightSelected(new Set());
      }
      setDraggingFile(null);
    }
  };

  const actionBarButtons = [
    {label:"👁 View", title:"View", onClick:doView, disabled:totalSelected===0},
    {label:"✏ Rename", title:"Rename", onClick:doRename, disabled:totalSelected!==1},
    {label:"⇄ Move", title:"Move to folder", onClick:doMove, disabled:totalSelected===0},
    {label:"📁 Folder", title:"New folder", onClick:doNewFolder, disabled:false},
    {label:"📂 Subfolder", title:"New subfolder", onClick:doNewSubfolder, disabled:!activeFolder},
    {label:"↓ Download", title:"Download", onClick:doDownload, disabled:totalSelected===0},
    {label:"🗑 Delete", title:"Delete", onClick:doDelete, disabled:totalSelected===0, danger:true},
    {label:"⚡ Defaults", title:"Create default folders", onClick:doDefaults, disabled:false},
  ];

  if(loading) return <div style={{textAlign:"center",padding:"40px 0",color:"#888",fontSize:13}}>Loading...</div>;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 100px)"}}>

      {/* ── Modals ── */}
      {showUpload&&<UploadModal folder={null} clientName={leftClient||rightClient} allClients={visibleClients} allFolders={folders} onClose={()=>setShowUpload(false)} onUploaded={()=>{}} isAdmin={isAdmin}/>}
      {showDocRequest&&<DocumentRequestModal client={null} initialClient={activeClient} onClose={()=>setShowDocRequest(false)} currentUserName={userEmail||"Frank Ficsori"} isAdmin={isAdmin}/>}

      {renameFile&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:12,padding:"20px 24px",width:380}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Rename File</div>
            <div style={{fontSize:11,color:"#888",marginBottom:10}}>{renameFile.name}</div>
            <input style={S.input} value={renameFileName} onChange={e=>setRenameFileName(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&saveRenameFile()}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button style={S.btn} onClick={()=>setRenameFile(null)}>Cancel</button>
              <button style={S.btnPrimary} onClick={saveRenameFile}>Save</button>
            </div>
          </div>
        </div>
      )}

      {renameFolder&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:12,padding:"20px 24px",width:340}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>Rename Folder</div>
            <input style={S.input} value={renameFolderName} onChange={e=>setRenameFolderName(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&saveRenameFolder()}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button style={S.btn} onClick={()=>setRenameFolder(null)}>Cancel</button>
              <button style={S.btnPrimary} onClick={saveRenameFolder}>Save</button>
            </div>
          </div>
        </div>
      )}

      {createFolderModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:12,padding:"20px 24px",width:380}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>
              {createFolderModal.parent?"New Subfolder":"New Folder"}
            </div>
            {createFolderModal.parent&&<div style={{fontSize:11,color:"#888",marginBottom:10}}>Under: {createFolderModal.parent.name}</div>}
            <input style={S.input} value={newFolderName} onChange={e=>setNewFolderName(e.target.value)} autoFocus placeholder="Folder name..." onKeyDown={e=>e.key==="Enter"&&createFolder()}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:12}}>
              <button style={S.btn} onClick={()=>setCreateFolderModal(null)}>Cancel</button>
              <button style={S.btnPrimary} onClick={createFolder}>Create</button>
            </div>
          </div>
        </div>
      )}

      {moveConfirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:14,padding:"22px 26px",width:440,maxHeight:"80vh",display:"flex",flexDirection:"column"}}>
            <div style={{fontSize:14,fontWeight:500,marginBottom:6}}>Move Files</div>
            <div style={{fontSize:12,color:"#555",marginBottom:14,background:"#f5f5f0",borderRadius:8,padding:"10px 12px"}}>
              <div style={{marginBottom:4,fontWeight:500}}>{moveConfirm.files.length} file{moveConfirm.files.length!==1?"s":""} selected:</div>
              {moveConfirm.files.slice(0,5).map(f=><div key={f.firestoreId} style={{fontSize:11,color:"#888"}}>{fileIcon(f.name)} {f.name}</div>)}
              {moveConfirm.files.length>5&&<div style={{fontSize:11,color:"#aaa"}}>...and {moveConfirm.files.length-5} more</div>}
            </div>
            <div style={{fontSize:11,color:"#888",marginBottom:6,fontWeight:500}}>Select destination folder:</div>
            <div style={{flex:1,overflowY:"auto",border:"0.5px solid #e0dfd8",borderRadius:8,marginBottom:14}}>
              {[...new Set(moveConfirm.files.map(f=>f.client))].map(client=>{
                const byId = {};
                folders.filter(f=>f.client===client).forEach(f=>{ byId[f.firestoreId]=f; });
                const getPath = (f) => {
                  const parts=[f.name]; let cur=f;
                  while(cur.parentId&&byId[cur.parentId]){cur=byId[cur.parentId];parts.unshift(cur.name);}
                  return parts.join(" › ");
                };
                const sortedFolders = folders.filter(f=>f.client===client)
                  .map(f=>({...f,path:getPath(f)}))
                  .sort((a,b)=>a.path.localeCompare(b.path));
                return (
                  <div key={client}>
                    <div style={{padding:"6px 10px",fontSize:10,color:"#888",fontWeight:600,background:"#f5f5f0",borderBottom:"0.5px solid #e0dfd8",textTransform:"uppercase",letterSpacing:"0.05em"}}>{client}</div>
                    <div onClick={()=>executeMoveFiles(null)}
                      style={{padding:"9px 14px",cursor:"pointer",fontSize:12,borderBottom:"0.5px solid #f5f5f0",display:"flex",alignItems:"center",gap:8}}
                      onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <span>📂</span><span>Root (no folder)</span>
                    </div>
                    {sortedFolders.map(f=>(
                      <div key={f.firestoreId} onClick={()=>executeMoveFiles(f)}
                        style={{padding:"9px 14px",paddingLeft:(14+f.path.split(" › ").length*10)+"px",cursor:"pointer",fontSize:12,borderBottom:"0.5px solid #f5f5f0",display:"flex",alignItems:"center",gap:8}}
                        onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{fontSize:13}}>{FOLDER_ICONS[f.name]||"📁"}</span>
                        <span style={{flex:1}}>{f.path}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button style={S.btn} onClick={()=>setMoveConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Top Bar ── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexShrink:0}}>
        <div style={{fontSize:14,fontWeight:500}}>Secure Files</div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          {[["files","📁 Files"],["audit","📋 Audit"]].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)} style={{...S.btn,background:view===v?"#1a1a18":"#fff",color:view===v?"#fff":"#555",border:`0.5px solid ${view===v?"#1a1a18":"#ccc"}`}}>{l}</button>
          ))}
          <button style={{...S.btn,color:"#534AB7",borderColor:"#C5C3F0",background:"#EEEDFE"}} onClick={()=>setShowDocRequest(true)}>📄 Request</button>
          <button style={S.btnPrimary} onClick={()=>setShowUpload(true)}>⬆ Upload</button>
        </div>
      </div>

      {view==="audit"?(
        <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,overflow:"auto",flex:1}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:"#fafaf8"}}>
              {["Action","File","Client","Folder","User","Time"].map(h=>(
                <th key={h} style={{textAlign:"left",padding:"8px 12px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:11}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {auditLog.length===0&&<tr><td colSpan={6} style={{textAlign:"center",padding:"32px",color:"#aaa"}}>No entries yet.</td></tr>}
              {auditLog.map(e=>(
                <tr key={e.firestoreId}>
                  <td style={{padding:"7px 12px",borderBottom:"0.5px solid #f5f5f0"}}><span style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:e.action==="deleted"?"#FCEBEB":"#EAF3DE",color:e.action==="deleted"?"#A32D2D":"#3B6D11",fontWeight:500}}>{e.action}</span></td>
                  <td style={{padding:"7px 12px",borderBottom:"0.5px solid #f5f5f0"}}>{e.fileName}</td>
                  <td style={{padding:"7px 12px",borderBottom:"0.5px solid #f5f5f0",color:"#888"}}>{e.client}</td>
                  <td style={{padding:"7px 12px",borderBottom:"0.5px solid #f5f5f0",color:"#888"}}>{e.folder}</td>
                  <td style={{padding:"7px 12px",borderBottom:"0.5px solid #f5f5f0",color:"#888"}}>{e.user}</td>
                  <td style={{padding:"7px 12px",borderBottom:"0.5px solid #f5f5f0",color:"#aaa"}}>{e.timestamp?new Date(e.timestamp).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ):(
        <div style={{display:"flex",flex:1,flexDirection:"column",overflow:"hidden",gap:0}}>

          {/* ── Client selectors row ── */}
          <div style={{display:"flex",gap:1,flexShrink:0,background:"#e0dfd8"}}>
            {/* Left client */}
            <div style={{flex:1,background:"#fff",padding:"6px 8px",display:"flex",alignItems:"center",gap:6}}
              onClick={()=>setActivePanel("left")}
              style={{flex:1,background:activePanel==="left"?"#fff":"#f5f5f0",padding:"6px 8px",display:"flex",alignItems:"center",gap:6,cursor:"pointer",borderBottom:activePanel==="left"?"2px solid #C0111F":"2px solid transparent"}}>
              <span style={{fontSize:11,color:"#888",flexShrink:0}}>Left:</span>
              <select style={{...S.input,fontSize:11,flex:1}} value={leftClient}
                onChange={e=>{setLeftClient(e.target.value);setLeftFolder(null);setLeftSelected(new Set());localStorage.setItem("ficsori_left_client",e.target.value);}}
                onClick={e=>e.stopPropagation()}>
                <option value="">Select client...</option>
                {visibleClients.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            {/* Divider */}
            <div style={{width:3,background:"#e0dfd8",flexShrink:0}}/>
            {/* Right client */}
            <div onClick={()=>setActivePanel("right")}
              style={{flex:1,background:activePanel==="right"?"#fff":"#f5f5f0",padding:"6px 8px",display:"flex",alignItems:"center",gap:6,cursor:"pointer",borderBottom:activePanel==="right"?"2px solid #C0111F":"2px solid transparent"}}>
              <span style={{fontSize:11,color:"#888",flexShrink:0}}>Right:</span>
              <select style={{...S.input,fontSize:11,flex:1}} value={rightClient}
                onChange={e=>{setRightClient(e.target.value);setRightFolder(null);setRightSelected(new Set());localStorage.setItem("ficsori_right_client",e.target.value);}}
                onClick={e=>e.stopPropagation()}>
                <option value="">Select client...</option>
                {visibleClients.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* ── Dual panels ── */}
          <div style={{display:"flex",flex:1,overflow:"hidden",gap:1,background:"#e0dfd8"}}>
            {/* Left panel */}
            <div style={{flex:1,display:"flex",overflow:"hidden",background:"#fff"}}
              onClick={()=>setActivePanel("left")}>
              <Panel side="left" label={leftClient} clients={visibleClients}
                folders={folders} files={files}
                selected={leftSelected} setSelected={setLeftSelected}
                activeFolder={leftFolder} setActiveFolder={setLeftFolder}
                onDragStart={handleDragStart}
                onDropFolder={handleFolderDrop("left")}
                dropHighlight={dropHighlightLeft}
                isAdmin={isAdmin}
                onRenameFolder={f=>{setRenameFolder(f);setRenameFolderName(f.name);}}
                onDeleteFolder={deleteFolder}
                onCreateSub={f=>{setCreateFolderModal({parent:f,client:leftClient});setNewFolderName("");}}
                search={leftSearch} setSearch={setLeftSearch}
              />
            </div>

            {/* Center divider */}
            <div style={{width:3,background:"#e0dfd8",flexShrink:0}}/>

            {/* Right panel */}
            <div style={{flex:1,display:"flex",overflow:"hidden",background:"#fff"}}
              onClick={()=>setActivePanel("right")}>
              <Panel side="right" label={rightClient} clients={visibleClients}
                folders={folders} files={files}
                selected={rightSelected} setSelected={setRightSelected}
                activeFolder={rightFolder} setActiveFolder={setRightFolder}
                onDragStart={handleDragStart}
                onDropFolder={handleFolderDrop("right")}
                dropHighlight={dropHighlightRight}
                isAdmin={isAdmin}
                onRenameFolder={f=>{setRenameFolder(f);setRenameFolderName(f.name);}}
                onDeleteFolder={deleteFolder}
                onCreateSub={f=>{setCreateFolderModal({parent:f,client:rightClient});setNewFolderName("");}}
                search={rightSearch} setSearch={setRightSearch}
              />
            </div>
          </div>

          {/* ── Action Bar (bottom) ── */}
          <div style={{background:"#1a2a4a",padding:"6px 10px",display:"flex",alignItems:"stretch",gap:5,flexShrink:0}}>
            {actionBarButtons.map(btn=>(
              <button key={btn.title} title={btn.title} onClick={btn.onClick}
                disabled={btn.disabled}
                style={{
                  flex:1,
                  background:btn.danger?"#7a1a1a":btn.disabled?"#162038":"#1e3360",
                  color:btn.danger?"#ffcccc":btn.disabled?"#6a7a9a":"#c8d8f0",
                  border:btn.danger?"1px solid #a03030":btn.disabled?"1px solid #1e2e4e":"1px solid #2a4a7a",
                  borderRadius:6,
                  fontSize:12,
                  fontWeight:600,
                  padding:"8px 4px",
                  cursor:btn.disabled?"default":"pointer",
                  fontFamily:"sans-serif",
                  whiteSpace:"nowrap",
                  textAlign:"center",
                  opacity:btn.disabled?0.5:1,
                  transition:"all 0.15s",
                  letterSpacing:"0.01em",
                }}>
                {btn.label}
              </button>
            ))}
          </div>
          <div style={{background:"#0f1e38",padding:"3px 10px",fontSize:10,color:"#6a8aaa",flexShrink:0,display:"flex",gap:12}}>
            <span>Active: <strong style={{color:"#8ab0d0"}}>{activePanel==="left"?"Left":"Right"} panel</strong>{activeFolder?` › ${activeFolder.name}`:""}</span>
            {totalSelected>0&&<span style={{color:"#a0c0e0"}}>{totalSelected} file{totalSelected!==1?"s":""} selected{leftSelected.size>0&&rightSelected.size>0?` (L:${leftSelected.size} R:${rightSelected.size})`:""}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
